import { useEffect, useState } from "react";
import {
    Radio, Loader2, Play, Zap, WifiOff, Wifi, KeyRound, Plus,
    CheckCircle2, AlertTriangle, Trash2, Pause, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "../../../components/ui/dialog";

const TIPO_LABEL = {
    api_pull: "API Pull",
    webhook_push: "Webhook Push",
    sftp_scheduled: "SFTP Scheduled",
};
const TIPO_ICON = {
    api_pull: Zap,
    webhook_push: Radio,
    sftp_scheduled: Wifi,
};
const STATUS_BADGE = {
    exitoso: "badge-exito",
    parcial: "badge-gold",
    error: "badge-error",
    procesando: "badge-mock",
};

function fmtDate(iso) {
    if (!iso) return "—";
    return iso.slice(0, 16).replace("T", " ");
}

export default function FuentesTab({ facility }) {
    const { hasRole } = useAuth();
    const [fuentes, setFuentes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creds, setCreds] = useState([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [testResult, setTestResult] = useState({});
    const [running, setRunning] = useState({});

    const canEdit = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.ADMIN_TI]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/facilities/${facility.id}/fuentes`);
            setFuentes(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudieron cargar fuentes");
        } finally { setLoading(false); }
    };

    const loadCreds = async () => {
        try {
            const { data } = await api.get("/vault/credentials");
            setCreds(data);
        } catch {
            // silent — user might not be admin_ti
        }
    };

    useEffect(() => { load(); loadCreds(); /* eslint-disable-next-line */ }, [facility.id]);

    const probar = async (fid) => {
        setTestResult((s) => ({ ...s, [fid]: { loading: true } }));
        try {
            const { data } = await api.post(`/fuentes/${fid}/probar-conexion`);
            setTestResult((s) => ({ ...s, [fid]: data }));
            data.ok
                ? toast.success(`Prueba OK — ${data.rows_count} registros`)
                : toast.error(`Fallo: ${data.error?.slice(0, 60)}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message;
            setTestResult((s) => ({ ...s, [fid]: { ok: false, error: msg } }));
            toast.error(msg);
        }
    };

    const ejecutar = async (fid) => {
        setRunning((s) => ({ ...s, [fid]: true }));
        try {
            const { data } = await api.post(`/fuentes/${fid}/ejecutar-ahora`);
            toast.success(`Ingesta: ${data.ingesta.estado} · ${data.ingesta.registros_recibidos} recibidos`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setRunning((s) => ({ ...s, [fid]: false }));
        }
    };

    const toggleActivo = async (fuente) => {
        try {
            await api.patch(`/fuentes/${fuente.id}`, { activo: !fuente.activo });
            toast.success(fuente.activo ? "Fuente pausada" : "Fuente reactivada");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const eliminar = async (fuente) => {
        if (!confirm(`Eliminar fuente "${fuente.nombre}"?`)) return;
        try {
            await api.delete(`/fuentes/${fuente.id}`);
            toast.success("Fuente eliminada");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-5" data-testid="tab-fuentes-content">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="font-display text-xl flex items-center gap-2">
                        <Radio className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                        Fuentes de datos
                        <span className="badge-mock text-[10px]">MOCK</span>
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Conexión automatizada con el originador. Reemplaza la subida manual de tape.
                        Los orígenes externos están simulados en <span className="hash-cell">/api/mock/originador</span>.
                    </p>
                </div>
                {canEdit && (
                    <CrearFuenteDialog
                        open={openCreate} setOpen={setOpenCreate}
                        facility={facility} creds={creds}
                        reload={() => { load(); }}
                    />
                )}
            </div>

            {loading && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando fuentes…
                </div>
            )}

            {!loading && fuentes.length === 0 && (
                <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                    Sin fuentes configuradas. Los operadores siguen usando ingesta manual.
                </div>
            )}

            <div className="grid gap-4">
                {fuentes.filter((f) => f.activo !== false).map((f) => {
                    const Icon = TIPO_ICON[f.tipo] || Radio;
                    const tr = testResult[f.id];
                    return (
                        <div
                            key={f.id}
                            className="rounded-md border border-border bg-card p-4 space-y-3"
                            data-testid={`fuente-card-${f.id}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <div className="h-9 w-9 rounded-md bg-[color:var(--brand-gold)]/10 flex items-center justify-center shrink-0">
                                        <Icon className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-medium">{f.nombre}</div>
                                            <span className="badge-gold text-[10px]">
                                                {TIPO_LABEL[f.tipo]}
                                            </span>
                                            <span className="badge-mock text-[10px]">MOCK</span>
                                            {f.errores_consecutivos > 0 && (
                                                <span className="badge-error text-[10px]">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    {f.errores_consecutivos} errores
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-3">
                                            {f.tipo !== "webhook_push" && (
                                                <span>Cada {f.frecuencia_min} min</span>
                                            )}
                                            <span>
                                                Última:{" "}
                                                <span className="tabular">{fmtDate(f.ultima_sincronizacion)}</span>
                                                {f.ultima_sincronizacion_status && (
                                                    <span className={`ml-2 ${STATUS_BADGE[f.ultima_sincronizacion_status] || "badge-mock"} text-[10px]`}>
                                                        {f.ultima_sincronizacion_status}
                                                    </span>
                                                )}
                                            </span>
                                            <span>Registros última: {f.ultima_sincronizacion_registros || 0}</span>
                                        </div>
                                    </div>
                                </div>
                                {canEdit && (
                                    <div className="flex gap-1 shrink-0">
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => toggleActivo(f)}
                                            data-testid={`fuente-toggle-${f.id}`}
                                            title={f.activo === false ? "Reactivar" : "Pausar"}
                                        >
                                            {f.activo === false ? <PlayCircle className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => eliminar(f)}
                                            data-testid={`fuente-delete-${f.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-[color:var(--brand-coral)]" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <div className="text-[11px] font-mono bg-muted/30 rounded p-2 break-all">
                                {f.tipo === "api_pull" && (
                                    <>GET {f.configuracion?.endpoint_url || "—"} · auth={f.configuracion?.auth_type}</>
                                )}
                                {f.tipo === "webhook_push" && (
                                    <>POST /api/webhook/ingesta/{f.id} · HMAC-SHA256 header {f.configuracion?.firma_header}</>
                                )}
                                {f.tipo === "sftp_scheduled" && (
                                    <>SFTP {f.configuracion?.path_remoto}/{f.configuracion?.pattern}</>
                                )}
                            </div>

                            {canEdit && (
                                <div className="flex gap-2 flex-wrap">
                                    {f.tipo !== "webhook_push" && (
                                        <Button
                                            size="sm" variant="outline"
                                            onClick={() => probar(f.id)}
                                            data-testid={`fuente-probar-${f.id}`}
                                        >
                                            {tr?.loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> :
                                                tr?.ok === true ? <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" /> :
                                                    tr?.ok === false ? <WifiOff className="h-3 w-3 mr-1 text-[color:var(--brand-coral)]" /> :
                                                        <Wifi className="h-3 w-3 mr-1" />}
                                            Probar conexión
                                        </Button>
                                    )}
                                    {f.tipo !== "webhook_push" && (
                                        <Button
                                            size="sm"
                                            onClick={() => ejecutar(f.id)}
                                            disabled={running[f.id]}
                                            data-testid={`fuente-ejecutar-${f.id}`}
                                        >
                                            {running[f.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
                                            Ejecutar ahora
                                        </Button>
                                    )}
                                    {f.tipo === "webhook_push" && (
                                        <SimularWebhookBtn fuente={f} reload={load} />
                                    )}
                                </div>
                            )}

                            {tr?.error && (
                                <div className="text-xs text-[color:var(--brand-coral)] p-2 bg-[color:var(--brand-coral)]/5 rounded">
                                    {tr.error}
                                </div>
                            )}
                            {tr?.ok && tr.preview && (
                                <div className="text-[11px] text-muted-foreground">
                                    Preview {tr.rows_count} filas · primer credito_id: <span className="hash-cell">{tr.preview?.[0]?.credito_id}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SimularWebhookBtn({ fuente, reload }) {
    const [busy, setBusy] = useState(false);
    const [n, setN] = useState(20);
    const [open, setOpen] = useState(false);

    const disparar = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/mock/originador/simular-webhook", {
                fuente_id: fuente.id, n_creditos: Number(n) || 20,
                seed_offset: Math.floor(Math.random() * 1000),
            });
            if (data.post_status_code === 200) {
                toast.success(`Webhook procesado: ${data.post_response?.registros_recibidos || "?"} registros`);
                reload();
            } else {
                toast.error(`HTTP ${data.post_status_code}: ${JSON.stringify(data.post_response).slice(0, 80)}`);
            }
            setOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" data-testid={`fuente-simular-webhook-${fuente.id}`}>
                    <Zap className="h-3 w-3 mr-1" /> Simular webhook MOCK
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Simular webhook desde originador MOCK</DialogTitle>
                    <DialogDescription>
                        Genera un payload firmado con HMAC-SHA-256 usando el secreto
                        del vault y lo envía al endpoint /api/webhook/ingesta.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Cantidad de créditos</Label>
                        <Input
                            type="number" min="1" max="500" value={n}
                            onChange={(e) => setN(e.target.value)}
                            data-testid="simular-webhook-n-input"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={disparar} disabled={busy} data-testid="simular-webhook-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CrearFuenteDialog({ open, setOpen, facility, creds, reload }) {
    const [tipo, setTipo] = useState("api_pull");
    const [nombre, setNombre] = useState("");
    const [frecuencia, setFrecuencia] = useState(15);
    const [notas, setNotas] = useState("");
    const [endpointUrl, setEndpointUrl] = useState("http://127.0.0.1:8001/api/mock/originador/creditos?n=100");
    const [authType, setAuthType] = useState("bearer");
    const [credRef, setCredRef] = useState("");
    const [hmacRef, setHmacRef] = useState("");
    const [pathRemoto, setPathRemoto] = useState("/app/uploads/mock_sftp");
    const [pattern, setPattern] = useState("*.csv");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!nombre.trim()) return toast.error("Nombre requerido");
        let configuracion = {};
        if (tipo === "api_pull") {
            configuracion = {
                endpoint_url: endpointUrl.trim(),
                auth_type: authType,
                credentials_ref: credRef && credRef !== "none" ? credRef : null,
            };
        } else if (tipo === "webhook_push") {
            if (!hmacRef || hmacRef === "none") return toast.error("HMAC secret ref requerido");
            configuracion = {
                hmac_secret_ref: hmacRef,
                firma_header: "X-Trafalgar-Signature",
                timestamp_header: "X-Trafalgar-Timestamp",
                tolerancia_min: 5,
            };
        } else if (tipo === "sftp_scheduled") {
            configuracion = {
                path_remoto: pathRemoto.trim(),
                pattern: pattern.trim(),
                delete_after: false,
            };
        }
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/fuentes`, {
                nombre, tipo, configuracion,
                frecuencia_min: Number(frecuencia) || 15,
                mapping_columnas: {},
                alerta_umbral_errores: 3,
                notas,
            });
            toast.success("Fuente creada");
            setOpen(false);
            setNombre(""); setNotas("");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const bearerCreds = creds.filter((c) => ["bearer_token", "api_key"].includes(c.tipo));
    const hmacCreds = creds.filter((c) => c.tipo === "hmac_secret");

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="fuente-crear-btn">
                    <Plus className="h-4 w-4 mr-2" /> Nueva fuente
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Nueva fuente de datos</DialogTitle>
                    <DialogDescription>
                        Configura una conexión automatizada con el originador para este facility.
                        Los orígenes externos son MOCK durante Fase 7.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    <div>
                        <Label>Tipo</Label>
                        <Select value={tipo} onValueChange={setTipo}>
                            <SelectTrigger data-testid="fuente-tipo-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="api_pull">API Pull</SelectItem>
                                <SelectItem value="webhook_push">Webhook Push (HMAC-SHA256)</SelectItem>
                                <SelectItem value="sftp_scheduled">SFTP Scheduled</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Nombre descriptivo</Label>
                        <Input
                            value={nombre} onChange={(e) => setNombre(e.target.value)}
                            placeholder="Ej: Originador ABC · Cartera principal"
                            data-testid="fuente-nombre-input"
                        />
                    </div>
                    {tipo !== "webhook_push" && (
                        <div>
                            <Label>Frecuencia (minutos)</Label>
                            <Input
                                type="number" min="5" max="1440" value={frecuencia}
                                onChange={(e) => setFrecuencia(e.target.value)}
                                data-testid="fuente-frecuencia-input"
                            />
                        </div>
                    )}
                    {tipo === "api_pull" && (
                        <>
                            <div>
                                <Label>Endpoint URL</Label>
                                <Input
                                    value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)}
                                    data-testid="fuente-endpoint-input"
                                />
                            </div>
                            <div>
                                <Label>Auth Type</Label>
                                <Select value={authType} onValueChange={setAuthType}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="bearer">Bearer Token</SelectItem>
                                        <SelectItem value="apikey">API Key Header</SelectItem>
                                        <SelectItem value="basic">Basic Auth</SelectItem>
                                        <SelectItem value="none">Sin auth</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {authType !== "none" && (
                                <div>
                                    <Label>Credencial (vault)</Label>
                                    <Select value={credRef} onValueChange={setCredRef}>
                                        <SelectTrigger data-testid="fuente-cred-select"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                        <SelectContent>
                                            {bearerCreds.length === 0 && (
                                                <SelectItem value="none" disabled>Sin credenciales — crea una primero en Vault</SelectItem>
                                            )}
                                            {bearerCreds.map((c) => (
                                                <SelectItem key={c.id} value={c.id}>
                                                    {c.nombre_descriptivo} ({c.tipo})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </>
                    )}
                    {tipo === "webhook_push" && (
                        <div>
                            <Label>HMAC Secret (vault)</Label>
                            <Select value={hmacRef} onValueChange={setHmacRef}>
                                <SelectTrigger data-testid="fuente-hmac-select"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                <SelectContent>
                                    {hmacCreds.length === 0 && (
                                        <SelectItem value="none" disabled>Sin secrets HMAC en vault</SelectItem>
                                    )}
                                    {hmacCreds.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre_descriptivo}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[10px] text-muted-foreground mt-1">
                                Endpoint: <span className="hash-cell">POST /api/webhook/ingesta/&lt;id&gt;</span> · Headers HMAC-SHA256
                            </p>
                        </div>
                    )}
                    {tipo === "sftp_scheduled" && (
                        <>
                            <div>
                                <Label>Path remoto (mock local)</Label>
                                <Input value={pathRemoto} onChange={(e) => setPathRemoto(e.target.value)} />
                            </div>
                            <div>
                                <Label>Patrón archivos</Label>
                                <Input value={pattern} onChange={(e) => setPattern(e.target.value)} />
                            </div>
                        </>
                    )}
                    <div>
                        <Label>Notas</Label>
                        <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button onClick={submit} disabled={busy} data-testid="fuente-submit-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear fuente"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
